import * as Schema from "effect/Schema";
import { TrimmedNonEmptyString, PositiveInt, NonNegativeInt } from "./baseSchemas.ts";
import { PreviewAutomationUnavailableError } from "./previewAutomation.ts";

export const DELEGATE_TASK_DEFAULT_MODEL = "openrouter/deepseek/deepseek-v4-flash-0731";

export const DelegateTaskInput = Schema.Struct({
  task: TrimmedNonEmptyString.check(Schema.isMaxLength(8000)),
  cwd: Schema.optional(TrimmedNonEmptyString),
  baseRef: Schema.optional(TrimmedNonEmptyString),
  scopeHint: Schema.optional(Schema.String.check(Schema.isMaxLength(2000))),
  model: Schema.optional(TrimmedNonEmptyString),
  allowBash: Schema.optional(Schema.Boolean),
  timeoutMs: Schema.optional(PositiveInt.check(Schema.isLessThanOrEqualTo(1_800_000))),
});
export type DelegateTaskInput = typeof DelegateTaskInput.Type;

export const DelegateTaskFileChange = Schema.Struct({
  path: TrimmedNonEmptyString,
  insertions: NonNegativeInt,
  deletions: NonNegativeInt,
});
export type DelegateTaskFileChange = typeof DelegateTaskFileChange.Type;

export const DelegateTaskResult = Schema.Struct({
  worktreePath: TrimmedNonEmptyString,
  branchName: TrimmedNonEmptyString,
  baseRef: TrimmedNonEmptyString,
  commitSha: Schema.NullOr(TrimmedNonEmptyString),
  filesChanged: Schema.Array(DelegateTaskFileChange),
  diffPatch: Schema.String,
  diffTruncated: Schema.Boolean,
  summary: Schema.String,
  modelUsed: TrimmedNonEmptyString,
  durationMs: NonNegativeInt,
});
export type DelegateTaskResult = typeof DelegateTaskResult.Type;

export const DelegateWorktreeDiscardInput = Schema.Struct({
  cwd: TrimmedNonEmptyString,
  worktreePath: TrimmedNonEmptyString,
  force: Schema.optional(Schema.Boolean),
});
export type DelegateWorktreeDiscardInput = typeof DelegateWorktreeDiscardInput.Type;

export const DelegateWorktreeDiscardResult = Schema.Struct({ removed: Schema.Boolean });
export type DelegateWorktreeDiscardResult = typeof DelegateWorktreeDiscardResult.Type;

export class DelegateRepositoryError extends Schema.TaggedErrorClass<DelegateRepositoryError>()(
  "DelegateRepositoryError",
  { operation: Schema.String, cwd: Schema.String, detail: Schema.String, cause: Schema.optional(Schema.Defect()) },
) {
  override get message(): string {
    return "Delegated task git operation failed in " + this.operation + " (" + this.cwd + "): " + this.detail;
  }
}

export class DelegateGenerationError extends Schema.TaggedErrorClass<DelegateGenerationError>()(
  "DelegateGenerationError",
  { operation: Schema.String, cwd: Schema.String, model: Schema.String, detail: Schema.String, cause: Schema.optional(Schema.Defect()) },
) {
  override get message(): string {
    return "Delegated task generation failed (" + this.model + " in " + this.cwd + "): " + this.detail;
  }
}

export class DelegateTimeoutError extends Schema.TaggedErrorClass<DelegateTimeoutError>()(
  "DelegateTimeoutError",
  { operation: Schema.String, cwd: Schema.String, timeoutMs: PositiveInt },
) {
  override get message(): string {
    return "Delegated task timed out after " + this.timeoutMs + "ms in " + this.operation + ".";
  }
}

export const DelegateTaskError = Schema.Union([
  DelegateRepositoryError,
  DelegateGenerationError,
  DelegateTimeoutError,
  PreviewAutomationUnavailableError,
]);
export type DelegateTaskError = typeof DelegateTaskError.Type;

export const ConsultModelInput = Schema.Struct({
  task: TrimmedNonEmptyString.check(Schema.isMaxLength(8000)),
  cwd: Schema.optional(TrimmedNonEmptyString),
  model: Schema.optional(TrimmedNonEmptyString),
  timeoutMs: Schema.optional(PositiveInt.check(Schema.isLessThanOrEqualTo(1_800_000))),
});
export type ConsultModelInput = typeof ConsultModelInput.Type;

export const ConsultModelResult = Schema.Struct({
  summary: Schema.String,
  modelUsed: TrimmedNonEmptyString,
  durationMs: NonNegativeInt,
});
export type ConsultModelResult = typeof ConsultModelResult.Type;

export const ConsultModelError = Schema.Union([
  DelegateGenerationError,
  DelegateTimeoutError,
  PreviewAutomationUnavailableError,
]);
export type ConsultModelError = typeof ConsultModelError.Type;
